import React, { HTMLAttributes, useEffect } from 'react';
import { ButtonGroup } from '@vwfs-bronson/bronson-react';
import { useFormikContext } from 'formik';
import _ from 'lodash';
import { TrackableComponent } from '..';
import { MatchMakerAnswers } from '../../../shared/types';
import { QuestionItem } from '../../lib/types/QuestionItem';

type FormikButtonGroupValue = string | number | boolean;

type OwnProps = {
  itemList: QuestionItem<FormikButtonGroupValue>[];
  propName: keyof MatchMakerAnswers;
  required?: boolean;
  auto?: boolean;
  small?: boolean;
  inputType?: 'checkbox' | 'radio';
  preserveValues?: boolean;
  fieldValueType?: 'array' | 'scalar';
  onValueChange?: (
    nextValue: FormikButtonGroupValue | FormikButtonGroupValue[] | undefined
  ) => void;
  'data-testid'?: string;
} & HTMLAttributes<HTMLDivElement>;

export const FormikButtonGroup = ({
  itemList,
  propName,
  required,
  auto = true,
  small = false,
  inputType = 'radio',
  preserveValues = false,
  fieldValueType = 'array',
  onValueChange,
  ...restProps
}: OwnProps): JSX.Element => {
  const visibleItems = _.filter(itemList, 'availableIn');
  const groupName = `formikButtonGroup-${String(propName)}`;
  const groupTestId =
    (restProps['data-testid'] as string) || `formikButtonGroup-${String(propName)}`;
  const { values, setFieldValue, setFieldTouched } = useFormikContext<MatchMakerAnswers>();

  const toValueArray = (fieldValue: MatchMakerAnswers[keyof MatchMakerAnswers]) => {
    if (Array.isArray(fieldValue)) {
      return fieldValue as (string | number | boolean)[];
    }

    if (_.isNil(fieldValue) || fieldValue === '') {
      return [];
    }

    return [fieldValue as string | number | boolean];
  };

  const selectedValues: FormikButtonGroupValue[] = toValueArray(
    values[propName]
  ) as FormikButtonGroupValue[];

  const isSameValue = (value: unknown, candidate: unknown) => String(value) === String(candidate);

  const isValueSelected = (value: unknown) =>
    selectedValues.some((selectedValue) => isSameValue(selectedValue, value));

  const toFieldValue = (
    nextValues: FormikButtonGroupValue[]
  ): FormikButtonGroupValue | FormikButtonGroupValue[] | undefined => {
    if (fieldValueType === 'scalar') {
      if (inputType === 'radio') {
        return nextValues[0];
      }

      return nextValues.length <= 1 ? nextValues[0] : nextValues;
    }

    return inputType === 'radio' ? nextValues.slice(0, 1) : nextValues;
  };

  useEffect(() => {
    const validValues: FormikButtonGroupValue[] = visibleItems.map(
      (item) => item.value as FormikButtonGroupValue
    );
    const currentValues: FormikButtonGroupValue[] = toValueArray(
      values[propName]
    ) as FormikButtonGroupValue[];
    const validCurrentValues = currentValues.filter((value) =>
      validValues.some((validValue) => isSameValue(value, validValue))
    );

    if (preserveValues) return;

    setFieldValue(propName, toFieldValue(validCurrentValues), false);
  }, []);

  const handleChangeItem = (e: React.ChangeEvent<HTMLInputElement>) => {
    const targetValue = (visibleItems.find((item) => String(item.value) === e.currentTarget.value)
      ?.value ?? e.currentTarget.value) as FormikButtonGroupValue;

    if (inputType === 'radio') {
      const nextFieldValue = toFieldValue(e.currentTarget.checked ? [targetValue] : []);

      setFieldValue(propName, nextFieldValue);
      setFieldTouched(propName, true, false);
      onValueChange?.(nextFieldValue);
      return;
    }

    const nextValues: FormikButtonGroupValue[] = e.currentTarget.checked
      ? isValueSelected(targetValue)
        ? selectedValues
        : [...selectedValues, targetValue]
      : selectedValues.filter((value) => !isSameValue(value, targetValue));

    const nextFieldValue = toFieldValue(nextValues);

    setFieldValue(propName, nextFieldValue);
    setFieldTouched(propName, true, false);
    onValueChange?.(nextFieldValue);
  };

  return (
    <ButtonGroup
      buttonGroupName={groupName}
      buttonGroupType={inputType}
      required={required}
      testId={groupTestId}
      auto={auto}
      small={small}
    >
      <>
        {visibleItems.map((item) => (
          <TrackableComponent
            key={`${String(propName)}-${String(item.value)}`}
            trackingComponent={inputType === 'checkbox' ? 'checkbox' : 'radiobutton'}
            trackingFunction={item.trackingName || ''}
          >
            <ButtonGroup.Item
              index={String(item.value)}
              value={String(item.value)}
              buttonGroupItemIcon={item.buttonGroupItemIcon}
              checked={isValueSelected(item.value as FormikButtonGroupValue)}
              onChange={handleChangeItem}
              onBlur={() => setFieldTouched(propName, true)}
              testId={`${groupTestId}-${String(item.value)}`}
            >
              {item.displayName}
            </ButtonGroup.Item>
          </TrackableComponent>
        ))}
      </>
    </ButtonGroup>
  );
};
